<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { TYPES } from '@/models/EchCode';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import KpNumberField from '@/components/KpNumberField.vue';
import { ReduktionAngrenzendeGebaeude } from '@/models/ReduktionAngrenzendeGebaeude';
import { createValidator, createValidators, validateNotEmpty } from '@/helpers/model_validators';
import KpTextAreaField from '@/components/KpTextAreaField.vue';
import KpEchCodeSelect from '@/components/KpEchCodeSelect.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<ReduktionAngrenzendeGebaeude | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    bauperiode: createValidator(
        computed(() => record.value?.bauperiode_id),
        [validateNotEmpty],
    ),
    reduktion_1_gebaeude: createValidator(
        computed(() => record.value?.reduktion_1_gebaeude),
        [validateNotEmpty],
    ),
    reduktion_2_gebaeude: createValidator(
        computed(() => record.value?.reduktion_2_gebaeude),
        [validateNotEmpty],
    ),
    reduktion_3_gebaeude: createValidator(
        computed(() => record.value?.reduktion_3_gebaeude),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(ReduktionAngrenzendeGebaeude);
    } else {
        try {
            const rec = await ReduktionAngrenzendeGebaeude.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_geb_reduktion_angrenzende_gebaeude_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_geb_reduktion_angrenzende_gebaeude' });
    }
}
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('reduktion_angerenzende_gebaeude') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpEchCodeSelect
                        v-model="record.bauperiode_id"
                        class="col-6"
                        fluid
                        :type="TYPES.bauperiode"
                        :label="$t('bauperiode')"
                        :invalid="!validators.bauperiode.isValid.value"
                        :error="validators.bauperiode.msg.value"
                    ></KpEchCodeSelect>
                    <KpNumberField
                        v-model="record.reduktion_1_gebaeude"
                        :label="$t('reduktion_1_gebaeude')"
                        class="col-6"
                        fluid
                        :invalid="!validators.reduktion_1_gebaeude.isValid.value"
                        :error="validators.reduktion_1_gebaeude.msg.value"
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.reduktion_2_gebaeude"
                        :label="$t('reduktion_2_gebaeude')"
                        class="col-6"
                        fluid
                        :invalid="!validators.reduktion_2_gebaeude.isValid.value"
                        :error="validators.reduktion_2_gebaeude.msg.value"
                    ></KpNumberField>
                    <KpNumberField
                        v-model="record.reduktion_3_gebaeude"
                        :label="$t('reduktion_3_gebaeude')"
                        class="col-6"
                        fluid
                        :invalid="!validators.reduktion_3_gebaeude.isValid.value"
                        :error="validators.reduktion_3_gebaeude.msg.value"
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpTextAreaField
                        v-model="record.bemerkung"
                        :label="$t('bemerkung')"
                        class="col-12"
                        fluid
                    ></KpTextAreaField>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
